import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, Popover, Typography } from '@mui/material';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useDroppable,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../theme/tokens';
import {
  GRID_COLUMNS,
  sizeSpan,
  spanToSize,
  widgetDefinition,
  type DashboardShell,
  type LayoutWidget,
  type WidgetSize,
  type WidgetWindow,
} from '../widgetLayout';
import type { DashboardLayoutController } from '../useDashboardLayout';
import { PANEL_PREFIX, WidgetDrawer } from './WidgetDrawer';

const { surface, line, ink, accent, signal, radius, shadow } = tokens;

export const widgetLabel = (t: (key: string) => string, labelKey: string): string => t(`dashboardLayout.widgets.${labelKey}`);

const GAP = 16;
const ADD_TILE = 'add-tile';

/** Grid columns are `GRID_COLUMNS[shell]`; below `md` everything stacks full width. */
const columnStyle = (shell: DashboardShell, size: WidgetSize) => ({
  xs: '1 / -1',
  md: `span ${sizeSpan(shell, size)}`,
});

interface WidgetGridProps {
  shell: DashboardShell;
  controller: DashboardLayoutController;
  nodes: Record<string, React.ReactNode>;
  /** Padding of the grid inside its frame; the vendor page has none of its own. */
  padding?: string;
}

const gridSx = (shell: DashboardShell) => ({
  position: 'relative',
  display: 'grid',
  gap: `${GAP}px`,
  gridTemplateColumns: { xs: 'minmax(0,1fr)', md: `repeat(${GRID_COLUMNS[shell]}, minmax(0,1fr))` },
  // Sparse placement: a later tile never jumps into a gap before an earlier one, so the
  // saved order, keyboard order and what the user sees stay the same.
  gridAutoFlow: 'row',
  alignItems: 'start',
});

/**
 * Renders a resolved widget list. Viewing is a plain grid. Editing is direct
 * manipulation: drag a tile to move it, drag its right edge to resize it, use its
 * menu for width, time range and hide, and drag from the Widgets panel to add.
 */
export const WidgetGrid: React.FC<WidgetGridProps> = ({ shell, controller, nodes, padding }) => {
  const { widgets, editing } = controller;
  const visible = widgets.filter((w) => nodes[w.id] != null);

  if (!editing) {
    return (
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: padding ?? 0, ...gridSx(shell) }}>
        {visible.map((w) => (
          <Box component="li" key={w.id} data-widget-id={w.id} sx={{ minWidth: 0, gridColumn: columnStyle(shell, w.size) }}>
            {nodes[w.id]}
          </Box>
        ))}
      </Box>
    );
  }
  return <EditableGrid shell={shell} controller={controller} nodes={nodes} padding={padding} visible={visible} />;
};

const GripIcon: React.FC = () => (
  <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor" aria-hidden>
    <circle cx="2" cy="2" r="1.5" />
    <circle cx="8" cy="2" r="1.5" />
    <circle cx="2" cy="8" r="1.5" />
    <circle cx="8" cy="8" r="1.5" />
    <circle cx="2" cy="14" r="1.5" />
    <circle cx="8" cy="14" r="1.5" />
  </svg>
);

const sizeLabelKey = (shell: DashboardShell, size: WidgetSize) => (shell === 'vendor' && size === 'narrow' ? 'half' : size);

const EditableGrid: React.FC<WidgetGridProps & { visible: LayoutWidget[] }> = ({ shell, controller, nodes, padding, visible }) => {
  const { t } = useTranslation();
  const tt = t as unknown as (key: string, options?: Record<string, unknown>) => string;
  const { widgets } = controller;
  const gridRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [resizingId, setResizingId] = useState<string | null>(null);
  const [menuFor, setMenuFor] = useState<{ id: string; anchor: HTMLElement } | null>(null);
  const stopResize = useRef<(() => void) | null>(null);
  useEffect(() => () => stopResize.current?.(), []);

  const labelOf = useCallback(
    (id: string) => {
      const def = widgetDefinition(shell, id);
      return def ? widgetLabel(tt, def.labelKey) : id;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shell, t]
  );
  const sizeName = (size: WidgetSize) => tt(`dashboardLayout.sizes.${sizeLabelKey(shell, size)}`);

  // A pointer drag starts after 6px so clicks on menus still work; touch waits 200ms so the page can scroll.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const isPanelId = (id: string) => id.startsWith(PANEL_PREFIX);
  const titleForActive = (id: string) => labelOf(isPanelId(id) ? id.slice(PANEL_PREFIX.length) : id);
  const positionOf = (id: string | undefined) => {
    const index = visible.findIndex((w) => w.id === id);
    return { position: index + 1, total: visible.length };
  };

  const announcements: Announcements = {
    onDragStart: ({ active }) => tt('dashboardLayout.announce.pickedUp', { title: titleForActive(String(active.id)) }),
    onDragOver: ({ active, over }) =>
      over && !isPanelId(String(active.id)) && over.id !== ADD_TILE
        ? tt('dashboardLayout.announce.moved', { title: titleForActive(String(active.id)), ...positionOf(String(over.id)) })
        : undefined,
    onDragEnd: ({ active, over }) =>
      over && !isPanelId(String(active.id)) && over.id !== ADD_TILE
        ? tt('dashboardLayout.announce.dropped', { title: titleForActive(String(active.id)), ...positionOf(String(over.id)) })
        : undefined,
    onDragCancel: ({ active }) => tt('dashboardLayout.announce.cancelled', { title: titleForActive(String(active.id)) }),
  };

  const onDragStart = ({ active }: DragStartEvent) => {
    setMenuFor(null);
    setActiveId(String(active.id));
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    if (!over) return; // dropped outside the grid: cancel
    const id = String(active.id);
    if (isPanelId(id)) {
      const widgetId = id.slice(PANEL_PREFIX.length);
      const index = over.id === ADD_TILE ? widgets.length : widgets.findIndex((w) => w.id === over.id);
      if (index >= 0) controller.insertAt(widgetId, index);
      return;
    }
    if (active.id === over.id) return;
    const visibleIds = visible.map((w) => w.id);
    const to = visibleIds.indexOf(String(over.id));
    if (to >= 0) controller.moveTo(id, to, visibleIds);
  };

  const startResize = (e: React.PointerEvent, widget: LayoutWidget) => {
    e.preventDefault();
    e.stopPropagation();
    const grid = gridRef.current;
    const def = widgetDefinition(shell, widget.id);
    if (!grid || !def) return;
    const cols = GRID_COLUMNS[shell];
    const colW = (grid.getBoundingClientRect().width - GAP * (cols - 1)) / cols + GAP;
    const startSpan = sizeSpan(shell, widget.size);
    const startX = e.clientX;
    let current = widget.size;
    setResizingId(widget.id);
    setMenuFor(null);
    const move = (ev: PointerEvent) => {
      const span = Math.max(1, Math.min(cols, startSpan + Math.round((ev.clientX - startX) / colW)));
      const size = spanToSize(shell, span);
      if (!size || !def.allowedSizes.includes(size) || size === current) return;
      current = size;
      controller.resize(widget.id, size);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      stopResize.current = null;
      setResizingId(null);
    };
    stopResize.current = up;
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };

  // Keyboard resize: the handle is a slider over the sizes the widget allows.
  const stepSize = (widget: LayoutWidget, delta: -1 | 1) => {
    const def = widgetDefinition(shell, widget.id);
    if (!def) return;
    const sizes = [...def.allowedSizes].sort((a, b) => sizeSpan(shell, a) - sizeSpan(shell, b));
    const next = sizes[sizes.indexOf(widget.size) + delta];
    if (next) controller.resize(widget.id, next);
  };

  const active = activeId && !isPanelId(activeId) ? visible.find((w) => w.id === activeId) : undefined;
  const activePanelId = activeId && isPanelId(activeId) ? activeId.slice(PANEL_PREFIX.length) : null;
  const showGuides = !!activeId || !!resizingId;
  const menuWidget = menuFor ? visible.find((w) => w.id === menuFor.id) : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      accessibility={{ announcements }}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', minWidth: 0 }}>
        <Box sx={{ flex: '1 1 auto', minWidth: 0, p: padding ?? 0 }}>
          <Box ref={gridRef} role="list" aria-label={tt('dashboardLayout.gridLabel')} sx={gridSx(shell)}>
            {showGuides && (
              <Box
                aria-hidden
                sx={{
                  display: { xs: 'none', md: 'grid' },
                  position: 'absolute',
                  inset: '-8px 0',
                  gridTemplateColumns: `repeat(${GRID_COLUMNS[shell]}, minmax(0,1fr))`,
                  gap: `${GAP}px`,
                  pointerEvents: 'none',
                  zIndex: 0,
                }}
              >
                {Array.from({ length: GRID_COLUMNS[shell] }, (_, i) => (
                  <Box key={i} sx={{ borderRadius: `${radius.card}px`, bgcolor: signal.info.row, border: `1px dashed ${accent.border}` }} />
                ))}
              </Box>
            )}

            <SortableContext items={visible.map((w) => w.id)} strategy={rectSortingStrategy}>
              {visible.map((w) => {
                const def = widgetDefinition(shell, w.id);
                return (
                  <SortableTile
                    key={w.id}
                    shell={shell}
                    widget={w}
                    label={labelOf(w.id)}
                    sizeName={sizeName(w.size)}
                    node={nodes[w.id]}
                    resizing={resizingId === w.id}
                    dragging={!!activeId}
                    canResize={(def?.allowedSizes.length ?? 0) > 1}
                    onMenu={(anchor) => setMenuFor((cur) => (cur?.id === w.id ? null : { id: w.id, anchor }))}
                    onResizeStart={(e) => startResize(e, w)}
                    onResizeKey={(delta) => stepSize(w, delta)}
                  />
                );
              })}
            </SortableContext>

            <AddTile onClick={controller.toggleDrawer} label={tt('dashboardLayout.addWidget')} />
          </Box>
        </Box>
        {controller.drawerOpen && <WidgetDrawer controller={controller} labelOf={labelOf} shell={shell} />}
      </Box>

      <DragOverlay dropAnimation={{ duration: 200, easing: 'ease' }}>
        {active ? (
          <Box sx={{ transform: 'rotate(1deg)', boxShadow: shadow.overlay, borderRadius: `${radius.card}px`, cursor: 'grabbing' }}>
            <TileCard label={labelOf(active.id)} sizeName={sizeName(active.size)} node={nodes[active.id]} />
          </Box>
        ) : activePanelId ? (
          <Box sx={{ px: 2, py: 1.25, bgcolor: surface.card, border: `1px solid ${accent.border}`, borderRadius: `${radius.control}px`, boxShadow: shadow.flyout, fontSize: 13, fontWeight: 600, color: ink.primary }}>
            {labelOf(activePanelId)}
          </Box>
        ) : null}
      </DragOverlay>

      <Popover
        open={!!menuFor && !!menuWidget}
        anchorEl={menuFor?.anchor ?? null}
        onClose={() => setMenuFor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            // Portal events bubble through React: keep a menu click from starting a tile drag.
            onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
            sx: { width: 232, mt: 0.5, p: '10px', display: 'flex', flexDirection: 'column', gap: '10px', border: `1px solid ${line.default}`, borderRadius: `${radius.card}px`, boxShadow: shadow.menu },
          },
        }}
      >
        {menuWidget && (
          <TileMenu
            shell={shell}
            widget={menuWidget}
            controller={controller}
            onClose={() => setMenuFor(null)}
            sizeName={sizeName}
          />
        )}
      </Popover>
    </DndContext>
  );
};

interface TileCardProps {
  label: string;
  sizeName: string;
  node: React.ReactNode;
  /** Present on the live tile only: the grip, the menu button and the hidden-while-dragging body. */
  grip?: React.ReactNode;
  onMenu?: (anchor: HTMLElement) => void;
  menuLabel?: string;
  /** The tile's own controls need clicks while editing (the Key numbers tile); every other body stays inert. */
  interactive?: boolean;
  /** Muted hint beside the title, for an interactive body. */
  note?: string;
}

/** A tile's header row (grip, title, size pill, menu) over the widget body. The body is inert while editing. */
const TileCard: React.FC<TileCardProps> = ({ label, sizeName, node, grip, onMenu, menuLabel, interactive, note }) => (
  <Box sx={{ bgcolor: surface.card, border: `1px solid ${line.strong}`, borderRadius: `${radius.card}px`, boxShadow: shadow.card, minWidth: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 52, pl: '20px', pr: '12px', borderBottom: `1px solid ${line.hairline}` }}>
      <Box sx={{ ml: '-10px', width: 18, height: 28, display: 'grid', placeItems: 'center', color: ink.faint }}>{grip ?? <GripIcon />}</Box>
      <Typography noWrap sx={{ fontSize: 14.5, fontWeight: 700, color: ink.primary }}>{label}</Typography>
      {note && (
        <Typography noWrap sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 0, fontSize: 12, color: ink.muted }}>{note}</Typography>
      )}
      <Box sx={{ flex: '1 1 auto' }} />
      <Box component="span" sx={{ fontSize: 11.5, fontWeight: 600, color: ink.muted, bgcolor: surface.tint, borderRadius: `${radius.chip}px`, px: '7px', py: '2px', whiteSpace: 'nowrap' }}>
        {sizeName}
      </Box>
      {onMenu && (
        <ButtonBase
          aria-label={menuLabel}
          aria-haspopup="menu"
          onClick={(e) => onMenu(e.currentTarget)}
          // A press on the button must not begin a tile drag.
          onPointerDown={(e) => e.stopPropagation()}
          sx={{ width: 30, height: 30, borderRadius: `${radius.small}px`, color: ink.bodyAlt, '&:hover': { bgcolor: surface.tint } }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <circle cx="5" cy="12" r="1.8" />
            <circle cx="12" cy="12" r="1.8" />
            <circle cx="19" cy="12" r="1.8" />
          </svg>
        </ButtonBase>
      )}
    </Box>
    <Box
      data-tile-body
      sx={{ p: '14px 20px 18px', pointerEvents: interactive ? 'auto' : 'none', position: 'relative' }}
      // Tile drags start from the header only: a press inside an interactive body (a number drag,
      // the picker, which bubbles through its portal) never reaches the tile's own drag listeners.
      onPointerDown={interactive ? (e) => e.stopPropagation() : undefined}
      onTouchStart={interactive ? (e) => e.stopPropagation() : undefined}
    >
      {node}
    </Box>
  </Box>
);

interface SortableTileProps {
  shell: DashboardShell;
  widget: LayoutWidget;
  label: string;
  sizeName: string;
  node: React.ReactNode;
  resizing: boolean;
  dragging: boolean;
  canResize: boolean;
  onMenu: (anchor: HTMLElement) => void;
  onResizeStart: (e: React.PointerEvent) => void;
  onResizeKey: (delta: -1 | 1) => void;
}

const SortableTile: React.FC<SortableTileProps> = ({ shell, widget, label, sizeName, node, resizing, dragging, canResize, onMenu, onResizeStart, onResizeKey }) => {
  const { t } = useTranslation();
  const tt = t as unknown as (key: string, options?: Record<string, unknown>) => string;
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: widget.id });
  const def = widgetDefinition(shell, widget.id);
  const interactive = !!def?.configKeys.includes('kpis');
  const sizes = def ? [...def.allowedSizes].sort((a, b) => sizeSpan(shell, a) - sizeSpan(shell, b)) : [];

  return (
    <Box
      ref={setNodeRef}
      role="listitem"
      data-widget-id={widget.id}
      {...listeners}
      sx={{
        gridColumn: columnStyle(shell, widget.size),
        position: 'relative',
        zIndex: 1,
        minWidth: 0,
        cursor: 'grab',
        touchAction: 'manipulation',
        transform: CSS.Translate.toString(transform),
        transition,
        outline: resizing ? `2px solid ${accent.main}` : 'none',
        borderRadius: `${radius.card}px`,
      }}
    >
      <Box sx={{ visibility: isDragging ? 'hidden' : 'visible' }}>
        <TileCard
          label={label}
          sizeName={sizeName}
          node={node}
          menuLabel={tt('dashboardLayout.tileOptions')}
          onMenu={onMenu}
          interactive={interactive}
          note={interactive ? tt('dashboardLayout.kpis.note') : undefined}
          grip={
            <ButtonBase
              ref={setActivatorNodeRef}
              {...attributes}
              aria-label={tt('dashboardLayout.moveTile', { title: label })}
              sx={{ width: 18, height: 28, color: 'inherit', cursor: 'grab' }}
            >
              <GripIcon />
            </ButtonBase>
          }
        />
      </Box>

      {isDragging && (
        <Box sx={{ position: 'absolute', inset: '-1px', borderRadius: `${radius.card}px`, bgcolor: signal.info.row, border: `2px dashed ${signal.info.solid}`, display: 'grid', placeItems: 'center', zIndex: 4 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: accent.hover }}>{label}</Typography>
        </Box>
      )}

      {resizing && (
        <Box sx={{ position: 'absolute', inset: '-1px', borderRadius: `${radius.card}px`, border: `2px solid ${accent.main}`, bgcolor: 'rgba(239,246,255,.55)', display: 'grid', placeItems: 'center', zIndex: 4, pointerEvents: 'none' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: ink.inverse, bgcolor: accent.main, borderRadius: `${radius.small}px`, px: '10px', py: '5px' }}>{sizeName}</Typography>
        </Box>
      )}

      {canResize && !dragging && (
        <Box
          role="slider"
          tabIndex={0}
          aria-label={tt('dashboardLayout.resizeTile', { title: label })}
          aria-valuemin={1}
          aria-valuemax={sizes.length}
          aria-valuenow={Math.max(1, sizes.indexOf(widget.size) + 1)}
          aria-valuetext={sizeName}
          title={tt('dashboardLayout.dragToResize')}
          onPointerDown={onResizeStart}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'ArrowRight') { e.preventDefault(); onResizeKey(1); }
            if (e.key === 'ArrowLeft') { e.preventDefault(); onResizeKey(-1); }
          }}
          sx={{
            display: { xs: 'none', md: 'grid' },
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: '-9px',
            width: 18,
            placeItems: 'center',
            cursor: 'ew-resize',
            zIndex: 5,
            touchAction: 'none',
            '&:hover > span, &:focus-visible > span': { bgcolor: accent.main, borderColor: accent.main },
            outline: 'none',
          }}
        >
          <Box component="span" sx={{ width: 6, height: 44, borderRadius: '4px', bgcolor: surface.card, border: `1px solid ${accent.border}`, boxShadow: shadow.card }} />
        </Box>
      )}
    </Box>
  );
};

const AddTile: React.FC<{ onClick: () => void; label: string }> = ({ onClick, label }) => {
  const { setNodeRef, isOver } = useDroppable({ id: ADD_TILE });
  return (
    <ButtonBase
      ref={setNodeRef}
      onClick={onClick}
      sx={{
        gridColumn: { xs: '1 / -1', md: 'span 1' },
        position: 'relative',
        zIndex: 1,
        minHeight: 140,
        flexDirection: 'column',
        gap: '6px',
        border: `1.5px dashed ${isOver ? signal.info.solid : ink.disabled}`,
        borderRadius: `${radius.card}px`,
        bgcolor: isOver ? signal.info.row : 'transparent',
        color: isOver ? accent.hover : ink.bodyAlt,
        '&:hover': { borderColor: signal.info.solid, bgcolor: signal.info.row, color: accent.hover },
      }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'inherit' }}>{label}</Typography>
    </ButtonBase>
  );
};

const Segmented: React.FC<{
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onPick: (value: string) => void;
}> = ({ label, value, options, onPick }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: ink.muted }}>{label}</Typography>
    <Box role="radiogroup" aria-label={label} sx={{ display: 'flex', gap: '2px', p: '2px', bgcolor: surface.tint, borderRadius: `${radius.small}px` }}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <ButtonBase
            key={o.value}
            role="radio"
            aria-checked={on}
            onClick={() => onPick(o.value)}
            sx={{
              flex: '1 1 0',
              height: 28,
              borderRadius: `${radius.chip}px`,
              fontSize: 12.5,
              fontWeight: 600,
              bgcolor: on ? surface.card : 'transparent',
              boxShadow: on ? '0 1px 2px rgba(15,23,42,.12)' : 'none',
              color: on ? ink.primary : ink.muted,
            }}
          >
            {o.label}
          </ButtonBase>
        );
      })}
    </Box>
  </Box>
);

const TileMenu: React.FC<{
  shell: DashboardShell;
  widget: LayoutWidget;
  controller: DashboardLayoutController;
  onClose: () => void;
  sizeName: (size: WidgetSize) => string;
}> = ({ shell, widget, controller, onClose, sizeName }) => {
  const { t } = useTranslation();
  const tt = t as unknown as (key: string) => string;
  const def = widgetDefinition(shell, widget.id);
  if (!def) return null;
  return (
    <>
      {def.allowedSizes.length > 1 && (
        <Segmented
          label={tt('dashboardLayout.size')}
          value={widget.size}
          options={def.allowedSizes.map((s) => ({ value: s, label: sizeName(s) }))}
          onPick={(s) => controller.resize(widget.id, s as WidgetSize)}
        />
      )}
      {def.configKeys.includes('window') && (
        <Segmented
          label={tt('dashboardLayout.timeRange')}
          value={widget.config.window ?? '12w'}
          options={(['4w', '12w', '26w'] as WidgetWindow[]).map((w) => ({ value: w, label: tt(`dashboardLayout.windowsShort.${w}`) }))}
          onPick={(w) => controller.configure(widget.id, { ...widget.config, window: w as WidgetWindow })}
        />
      )}
      <Box sx={{ height: '1px', bgcolor: line.hairline }} />
      <ButtonBase
        onClick={() => {
          onClose();
          controller.remove(widget.id);
        }}
        sx={{ justifyContent: 'flex-start', gap: 1, height: 32, px: 1, borderRadius: `${radius.small}px`, fontSize: 13, fontWeight: 600, color: signal.problem.ink, '&:hover': { bgcolor: signal.problem.tint } }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M4 7h16M9 7V4.5h6V7M6.5 7l1 12.5h9l1-12.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {tt('dashboardLayout.hideTile')}
      </ButtonBase>
    </>
  );
};
